import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import CredSection from './cred-section';
import VideoPlayer from './main-section/video-player.component';
import OrganizationSection from './organization-section';
import ReccoSection from './recco-section';
import Sidebar from './sidebar';
import SkillSection from './skill-section';
import { Icons } from '../../../assets';
import { AccountContext, AccountContextInterface } from '../../../contexts';
import { Account } from '../../../models';
import { AccountOrganization } from '../../../models/organization.model';
import { UserSkill } from '../../../models/skill.model';
import { Tab } from '../../../types';

type AuraSideBarProps = {
  accountId: string;
};

const AuraSection: FC<AuraSideBarProps> = ({ accountId }) => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const scriptRef = useRef<HTMLDivElement>(null);
  const [updatedAccount, setUpdatedAccount] = useState<Account>();
  const [selectedOrganization, setSelectedOrganization] = useState<AccountOrganization>();
  const [activeTab, setActiveTab] = useState<Tab>(Tab.Overview);
  const [selectedSkill, setSelectedSkill] = useState<UserSkill>();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: accountDetails } = await accountService.getAccount(accountId);
        setUpdatedAccount(accountDetails);
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
  }, [accountId, accountService]);

  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const el = scriptRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }
  }, [updatedAccount?.elevatorPitch?.introScript]);

  const handleOrganizationClick = (organization?: AccountOrganization, type?: Tab.Creds | Tab.Reccos) => {
    setSelectedSkill(undefined);
    organization && setSelectedOrganization(organization);
    type && setActiveTab(type === Tab.Creds ? Tab.Creds : Tab.Reccos);
  };

  const handleSkillClick = (skill: UserSkill, type: Tab.Creds | Tab.Reccos) => {
    setSelectedOrganization(undefined);
    setSelectedSkill(skill);
    if (activeTab === type) {
      setActiveTab(Tab.Overview);
      setTimeout(() => setActiveTab(type), 0);
    } else {
      setActiveTab(type);
    }
  };

  const handleTabClick = (type: Tab) => {
    setSelectedOrganization(undefined);
    setSelectedSkill(undefined);
    setActiveTab(type);
  };

  return (
    <div className="flex justify-center md:px-4">
      <div className="flex flex-col md:flex-row max-w-6xl pb-4 w-full">
        <Sidebar accountId={accountId} onSkillClick={handleSkillClick} />
        <div className="bg-white flex-1 flex-col gap-4 px-4 py-6 md:border-l-2 border-[#1C1C1C1A]">
          <div className="border-b border-[#F2F2F2] flex gap-2 mb-4 pt-2 px-4">
            <button
              className={`flex-1 p-2 ${activeTab === Tab.Overview ? 'border-b-2 border-[#2E7CF6] text-[#2E7CF6]' : ''}`}
              onClick={() => handleTabClick(Tab.Overview)}>
              {Tab.Overview}
            </button>
            <button
              className={`flex-1 p-2 ${activeTab === Tab.Creds ? 'border-b-2 border-[#2E7CF6] text-[#2E7CF6]' : ''}`}
              onClick={() => handleTabClick(Tab.Creds)}>
              {Tab.Creds}
            </button>
            <button
              className={`flex-1 p-2 ${activeTab === Tab.Reccos ? 'border-b-2 border-[#2E7CF6] text-[#2E7CF6]' : ''}`}
              onClick={() => handleTabClick(Tab.Reccos)}>
              {Tab.Reccos}
            </button>
          </div>
          {activeTab === Tab.Overview && (
            <>
              {updatedAccount?.elevatorPitch?.resource && updatedAccount?.elevatorPitch?.resource.url && (
                <div className="mb-4">
                  <div className="flex justify-between mb-2">
                    <div className="flex gap-2 items-center">
                      <img alt="recco real" src={Icons.RECCO_REAL} className="h-6 w-6" />
                      <p className="font-semibold text-lg text-primary-gray">Recco Real</p>
                    </div>
                    {updatedAccount.elevatorPitch?.cred && (
                      <div className="flex gap-4 items-center">
                        {updatedAccount.elevatorPitch.cred.hifiCount > 0 && (
                          <div className="flex gap-2 items-center">
                            <img src={Icons.HI_FI_ICON} className="h-6 w-6" alt="Hifi" />
                            <p className="font-semibold text-sm text-[#2B2B2B]">{updatedAccount.elevatorPitch.cred.hifiCount}</p>
                          </div>
                        )}
                        {updatedAccount.elevatorPitch.cred.reccoCount > 0 && (
                          <div
                            className="flex gap-2 items-center cursor-pointer"
                            onClick={() => handleOrganizationClick(undefined, Tab.Reccos)}>
                            <img src={Icons.RECCO_ICON_V2} className="h-6 w-6" alt="Recco" />
                            <p className="font-semibold text-sm text-[#2B2B2B]">{updatedAccount.elevatorPitch.cred.reccoCount}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="aspect-video bg-[#F5F5F7] p-2 rounded-2xl">
                    <VideoPlayer src={updatedAccount?.elevatorPitch.resource?.url} className="h-full w-full" />
                    {updatedAccount.elevatorPitch.introScript && (
                      <div className="font-medium mt-2 p-2 text-sm text-[#2B2B2B]">
                        <div
                          ref={scriptRef}
                          className={`relative transition-all duration-200 ease-in-out ${isExpanded ? '' : 'line-clamp-2'}`}>
                          {updatedAccount.elevatorPitch.introScript}
                          {!isExpanded && isOverflowing && (
                            <span
                              className="cursor-pointer text-blue-500"
                              onClick={toggleDescription}
                              style={{
                                position: 'absolute',
                                bottom: '0',
                                right: '0',
                                backgroundColor: '#F5F5F7',
                                paddingLeft: '4px',
                              }}>
                              ...read more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <SkillSection accountId={accountId} onSkillClick={handleSkillClick} />
              <OrganizationSection accountId={accountId} onOrganizationClick={handleOrganizationClick} />
            </>
          )}
          {activeTab === Tab.Creds && (
            <CredSection accountId={accountId} defaultOrganization={selectedOrganization} defaultSkill={selectedSkill} />
          )}
          {activeTab === Tab.Reccos && (
            <ReccoSection accountId={accountId} defaultOrganization={selectedOrganization} defaultSkill={selectedSkill} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AuraSection;
